import React, { useEffect, useRef, useState } from 'react';
import { renderAsync } from 'docx-preview';
import { FileText } from 'lucide-react';
import { getAuthenticatedFileBlob } from '../services/api';

interface DocxPreviewProps {
  filename: string;
  title: string;
}

const DocxPreview: React.FC<DocxPreviewProps> = ({ filename, title }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadDocx = async () => {
      if (!containerRef.current) return;
      setLoading(true);
      setError(null);

      try {
        const arrayBuffer = await getAuthenticatedFileBlob(filename);
        if (cancelled || !containerRef.current) return;

        await renderAsync(arrayBuffer, containerRef.current, undefined, {
          className: 'docx-preview-content',
          inWrapper: true,
          ignoreWidth: true,
          ignoreHeight: true,
          ignoreFonts: false,
          breakPages: true,
          ignoreLastRenderedPageBreak: true,
          experimental: false,
        });
        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to render DOCX:', err);
          setError('Failed to render document preview');
          setLoading(false);
        }
      }
    };

    loadDocx();
    return () => { cancelled = true; };
  }, [filename]);

  return (
    <div className="relative w-full h-full overflow-auto bg-gray-100">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
          <div className="text-center">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500">Rendering document...</p>
          </div>
        </div>
      )}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
          <div className="text-center">
            <FileText className="w-12 h-12 text-red-400 mx-auto mb-2" />
            <p className="text-red-500">{error}</p>
          </div>
        </div>
      )}
      <div ref={containerRef} title={title} />
    </div>
  );
};

export default DocxPreview;
